import './assistant-records.css';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useOrganization, useSession, useUser } from '@clerk/clerk-react';
import { toast } from 'sonner';
import { TagIcon, UsersIcon } from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/button';
import Checkbox from '../../../ui/checkbox';
import { getAuthToken } from '../../../../utils/auth';
import {
  getContactSelectionChunk,
  getContactSelectionRows,
  setContactSelectionExclusions,
  type ContactSelectionRow,
} from '../../../../api/dashboardAssistantHistory';
import { notifyOrgContactTagsUpdated } from '../../../../utils/orgContactTagsEvents';
import { getActiveOrganizationContext } from '../lib/organizationContext';
import {
  ContactSelectionExclusionQueue,
  type ContactSelectionExclusionSnapshot,
} from '../lib/contactSelectionExclusionQueue';
import type { ContactSelectionPreviewPayload } from '../types';
import {
  accountsClient,
  isAccountsRouteConfigured,
} from '../../../../lib/trpc/accounts';

// AI Tag Automation (initiatives/ai-tag-automation) — the PreviewCard rendered
// for a `resolve_contact_selection` tool result. The AI only PREVIEWS who
// matches; tagging happens here, gated by an explicit Apply click. The id set
// is frozen server-side (selectionToken); Apply pulls it in 500-id pages via
// getContactSelectionChunk and writes each page through the existing
// POST /contacts/tags (same path as ContactTagsModal). Keep the chunk size in
// sync with MAX_CONTACTS_PER_REQUEST in contact_tagger.py.
const CONTACTS_PER_REQUEST = 500;
const CONTACTS_BACKEND = import.meta.env.VITE_APP_DASHBOARD_CONTACTS_URL;

// Keep in sync with org_contact_tags.py + src/utils/contactTags.ts. (This card
// keeps a local copy deliberately — see the tagging plan's "out of scope".)
const MAX_TAG_LENGTH = 50;
const TAG_PATTERN = /^[A-Za-z0-9 &_-]+$/;

type ApplyAction = 'add' | 'remove';

type ApplyResult = {
  updated: number;
  unchanged: number;
  skipped: number;
};

// The turn the assistant waits for before it drafts a segment from a reviewed
// list (resolveContactSelectionTool). The button sends it so nobody has to type it.
export const USE_LIST_MESSAGE = 'The list is ready.';

// Page size for the full reviewable table (server-materialized rows).
const ROWS_PAGE_SIZE = 200;

function dictionaryValues(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((entry) => {
      if (typeof entry === 'string') return entry;
      if (entry && typeof (entry as { value?: unknown }).value === 'string') {
        return (entry as { value: string }).value;
      }
      return null;
    })
    .filter((v): v is string => Boolean(v));
}

export function ContactSelectionPreviewCard({
  payload,
  threadId,
  onUseList,
  useListDisabled = false,
}: {
  payload: ContactSelectionPreviewPayload;
  threadId: string;
  // Sends a chat message and resolves to whether it went; only the chat
  // panel passes it, and only to its newest segment selection (ADM-197).
  onUseList?: (message: string) => Promise<boolean>;
  useListDisabled?: boolean;
}) {
  // The dashboard doesn't reliably set Clerk's client-side "active
  // organization", so plain useOrganization() can be null here. Resolve the
  // org the same way useDashboardAssistantChat does (org → session claim →
  // single membership) — with only useOrganization(), applyTags silently
  // no-oped for users without an active-org record (tag created, nobody
  // assigned).
  const { organization } = useOrganization();
  const { session: clerkSession } = useSession();
  const { user } = useUser();
  const memberships = user?.organizationMemberships || [];
  const { organizationId: resolvedOrganizationId } = getActiveOrganizationContext({
    memberships,
    organization,
    sessionOrganizationId: clerkSession?.lastActiveOrganizationId,
  });
  const organizationId = resolvedOrganizationId || '';
  const actorId = user?.id || '';

  const [action, setAction] = useState<ApplyAction>('add');
  const [dictionary, setDictionary] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [applying, setApplying] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [result, setResult] = useState<ApplyResult | null>(null);
  // Contacts the user unchecked in the table — excluded from apply.
  const [excludedIds, setExcludedIds] = useState<Set<string>>(() => new Set());
  const [exclusionRevision, setExclusionRevision] = useState<number | null>(null);
  const [exclusionsPending, setExclusionsPending] = useState(false);
  const [selectionHydrated, setSelectionHydrated] = useState(false);
  const [selectionReviewError, setSelectionReviewError] = useState<string | null>(null);
  const [reviewedExclusions, setReviewedExclusions] = useState<ContactSelectionExclusionSnapshot | null>(null);
  // The exclusion revision "Use this list" was sent for, so one list is sent once.
  const [usedListRevision, setUsedListRevision] = useState<number | null>(null);
  // "Create a new tag" — pre-filled with the model's suggestion when the
  // request implied a new tag concept.
  const [newTagName, setNewTagName] = useState<string>(payload.suggestedTagNames?.[0] || '');
  const [creating, setCreating] = useState(false);
  // Step gate: the tag chooser (create new / pick existing / apply) only
  // appears once the user has confirmed the contact list.
  const [confirmed, setConfirmed] = useState(false);

  const matchedCount = payload.matchedCount || 0;
  const cap = payload.cap || 0;
  // A lookup only shows who matches; a segment's list is reviewed here and
  // saved through the segment proposal. Only a tag selection offers tagging
  // (QA 2026-10-12, F-26). Missing means tag, as before the field existed.
  const purpose = payload.purpose ?? 'tag';
  const canApply = Boolean(payload.selectionToken) && !payload.overCap && matchedCount > 0;
  const canExclude = canApply && purpose !== 'lookup';
  const selectionIdentity = `${actorId}\u0000${organizationId}\u0000${threadId}\u0000${payload.selectionToken ?? ''}`;
  const identityRef = useRef(selectionIdentity);
  identityRef.current = selectionIdentity;
  const queueRef = useRef<ContactSelectionExclusionQueue | null>(null);
  const exclusionRevisionRef = useRef<number | null>(null);
  const exclusionHydratedRef = useRef(false);
  const exclusionsPendingRef = useRef(false);
  const excludedIdsRef = useRef<Set<string>>(new Set());
  const sample = payload.sampleContacts || [];

  // Full reviewable table: page the server-materialized display rows. The
  // small displayPayload sample is only the fast-paint / expired-selection
  // fallback.
  const [rows, setRows] = useState<ContactSelectionRow[]>([]);
  const [rowsDone, setRowsDone] = useState(false);
  const [rowsLoading, setRowsLoading] = useState(false);
  // Why the full list is unavailable (e.g. the 1h selection TTL lapsed) —
  // surfaced in the footer instead of silently showing the sample.
  const [rowsError, setRowsError] = useState<string | null>(null);
  const rowsCursorRef = useRef(0);

  const installExclusionQueue = useCallback((snapshot: ContactSelectionExclusionSnapshot) => {
    if (!payload.selectionToken || !organizationId || !actorId || !threadId) return;
    const identity = selectionIdentity;
    queueRef.current?.stop();
    exclusionRevisionRef.current = snapshot.revision;
    exclusionHydratedRef.current = true;
    excludedIdsRef.current = new Set(snapshot.excludedContactIds);
    setExclusionRevision(snapshot.revision);
    setExcludedIds(new Set(snapshot.excludedContactIds));
    setSelectionHydrated(true);
    queueRef.current = new ContactSelectionExclusionQueue(
      snapshot,
      (expectedRevision, excludedContactIds) => setContactSelectionExclusions({
        organizationId,
        threadId,
        token: payload.selectionToken!,
        expectedRevision,
        excludedContactIds,
      }),
      {
        onAcknowledged: (acknowledged) => {
          if (identityRef.current !== identity) return;
          exclusionRevisionRef.current = acknowledged.revision;
          excludedIdsRef.current = new Set(acknowledged.excludedContactIds);
          setExclusionRevision(acknowledged.revision);
          setExcludedIds(new Set(acknowledged.excludedContactIds));
        },
        onConflict: (current) => {
          if (identityRef.current !== identity) return;
          exclusionRevisionRef.current = current.revision;
          excludedIdsRef.current = new Set(current.excludedContactIds);
          setExclusionRevision(current.revision);
          setExcludedIds(new Set(current.excludedContactIds));
          setReviewedExclusions(null);
          setConfirmed(false);
          setSelectionReviewError('This selection changed in another tab. Refresh the review before confirming or applying.');
        },
        onError: (error, acknowledged) => {
          if (identityRef.current !== identity) return;
          exclusionRevisionRef.current = acknowledged.revision;
          excludedIdsRef.current = new Set(acknowledged.excludedContactIds);
          setExclusionRevision(acknowledged.revision);
          setExcludedIds(new Set(acknowledged.excludedContactIds));
          setReviewedExclusions(null);
          setConfirmed(false);
          setSelectionReviewError(error instanceof Error ? error.message : 'Failed to save the current exclusions. Refresh the review and try again.');
        },
        onPendingChange: (pending) => {
          if (identityRef.current !== identity) return;
          exclusionsPendingRef.current = pending;
          setExclusionsPending(pending);
        },
      },
      threadId,
    );
  }, [actorId, organizationId, payload.selectionToken, selectionIdentity, threadId]);

  const loadMoreRows = useCallback(async (force = false) => {
    if (!canApply || !payload.selectionToken || !organizationId || (!force && (rowsLoading || rowsDone))) return;
    setRowsLoading(true);
    try {
      const requestIdentity = selectionIdentity;
      const page = await getContactSelectionRows({
        organizationId,
        threadId,
        token: payload.selectionToken,
        cursor: rowsCursorRef.current,
        limit: ROWS_PAGE_SIZE,
      });
      if (identityRef.current !== requestIdentity) return;
      if (!exclusionHydratedRef.current) {
        installExclusionQueue({
          revision: page.exclusionRevision,
          excludedContactIds: page.excludedContactIds,
        });
      } else if (exclusionRevisionRef.current !== page.exclusionRevision && !exclusionsPendingRef.current) {
        installExclusionQueue({
          revision: page.exclusionRevision,
          excludedContactIds: page.excludedContactIds,
        });
        setSelectionReviewError('This selection changed while its rows were loading. Review the current exclusions again.');
      }
      rowsCursorRef.current = page.nextCursor;
      setRows((prev) => {
        const seen = new Set(prev.map((r) => r.id));
        return [...prev, ...page.rows.filter((r) => !seen.has(r.id))];
      });
      setRowsDone(page.done || page.rows.length === 0);
    } catch (err) {
      // Expired/legacy selections have no rows to serve — fall back to the
      // sample, but say so instead of quietly showing a partial list.
      console.warn('[ContactSelectionPreviewCard] rows fetch failed', err);
      if (identityRef.current === selectionIdentity) {
        setRowsError(err instanceof Error ? err.message : 'Failed to load the full list.');
      }
      if (identityRef.current === selectionIdentity) setRowsDone(true);
    } finally {
      if (identityRef.current === selectionIdentity) setRowsLoading(false);
    }
  }, [canApply, installExclusionQueue, organizationId, payload.selectionToken, rowsDone, rowsLoading, selectionIdentity, threadId]);

  useEffect(() => {
    queueRef.current?.stop();
    queueRef.current = null;
    exclusionRevisionRef.current = null;
    exclusionHydratedRef.current = false;
    exclusionsPendingRef.current = false;
    excludedIdsRef.current = new Set();
    rowsCursorRef.current = 0;
    setRows([]);
    setRowsDone(false);
    setRowsError(null);
    setExcludedIds(new Set());
    setExclusionRevision(null);
    setExclusionsPending(false);
    setSelectionHydrated(false);
    setSelectionReviewError(null);
    setReviewedExclusions(null);
    setUsedListRevision(null);
    setConfirmed(false);
    setApplying(false);
    setCreating(false);
    setResult(null);
    setProgress(null);
    setDictionary([]);
    setSelectedTags([]);
    setAction('add');
    setNewTagName(payload.suggestedTagNames?.[0] || '');
    if (canApply && payload.selectionToken && organizationId && actorId && threadId) {
      void loadMoreRows(true);
    }
    return () => queueRef.current?.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionIdentity]);

  const shownRows: ContactSelectionRow[] = rows.length > 0 ? rows : sample;

  // Load the org tag dictionary so the user can pick which tag to apply/remove
  // (remove accepts any tag, but offering the dictionary keeps it consistent).
  useEffect(() => {
    if (!canApply || purpose !== 'tag' || !isAccountsRouteConfigured('GET /org-contact-tags')) return;
    let cancelled = false;
    (async () => {
      try {
        const token = await getAuthToken();
        const res = await accountsClient.request('GET /org-contact-tags', {
          authorization: token,
          input: {},
        });
        if (!res.ok) throw new Error('Failed to fetch tags');
        const data = await res.json();
        if (!('tags' in data)) throw new Error('Failed to fetch tags');
        if (!cancelled) setDictionary(dictionaryValues(data.tags));
      } catch (err) {
        console.error('[ContactSelectionPreviewCard] dictionary fetch failed', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canApply, purpose, selectionIdentity]);

  const toggleTag = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
    );
  };

  // Core apply loop, parameterized by the tags + action so both "apply existing"
  // and "create & apply" reuse it. Pulls frozen id pages from the server and
  // writes each via /contacts/tags (server enforces org-scope + MANAGE_CONTACTS).
  const applyTags = useCallback(
    async (tagsToApply: string[], actionToUse: ApplyAction) => {
      if (!payload.selectionToken || !tagsToApply.length || !reviewedExclusions) return;
      // Never fail silently — a returned-early apply looks exactly like
      // "nothing happened" to the user.
      if (!organizationId) {
        toast.error('No active organization context — reload the dashboard and try again.');
        return;
      }
      if (!CONTACTS_BACKEND) {
        toast.error('Contacts backend is not configured.');
        return;
      }
      setApplying(true);
      setResult(null);
      setProgress({ done: 0, total: matchedCount });

      let cursor = 0;
      let updated = 0;
      let unchanged = 0;
      let skipped = 0;
      const requestIdentity = selectionIdentity;
      const reviewedExcludedIds = new Set(reviewedExclusions.excludedContactIds);

      try {
        const token = await getAuthToken();
        if (identityRef.current !== requestIdentity) return;
        for (;;) {
          const chunk = await getContactSelectionChunk({
            organizationId,
            threadId,
            token: payload.selectionToken,
            expectedExclusionRevision: reviewedExclusions.revision,
            cursor,
            limit: CONTACTS_PER_REQUEST,
          });
          if (identityRef.current !== requestIdentity) return;
          const pageIds = chunk.contactIds.filter((id) => !reviewedExcludedIds.has(id));
          if (pageIds.length > 0) {
            const res = await fetch(`${CONTACTS_BACKEND}/contacts/tags`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: token },
              body: JSON.stringify({ action: actionToUse, tags: tagsToApply, contactIds: pageIds }),
            });
            const data = await res.json().catch(() => ({}));
            if (identityRef.current !== requestIdentity) return;
            if (!res.ok) {
              throw new Error(data?.error ?? 'Failed to apply tags');
            }
            updated += Number(data.updated) || 0;
            unchanged += Number(data.unchanged) || 0;
            if (Array.isArray(data.skipped)) skipped += data.skipped.length;
          }
          cursor = chunk.nextCursor;
          setProgress({ done: Math.min(cursor, chunk.total), total: chunk.total });
          if (chunk.done) break;
        }

        if (identityRef.current !== requestIdentity) return;
        setResult({ updated, unchanged, skipped });
        // Usage counts changed — let Settings → Tags refresh live.
        notifyOrgContactTagsUpdated();
        const tagNoun = tagsToApply.length === 1 ? 'tag' : 'tags';
        toast.success(
          actionToUse === 'add'
            ? `Added ${tagsToApply.length} ${tagNoun} to ${updated} contact${updated === 1 ? '' : 's'}.`
            : `Removed ${tagsToApply.length} ${tagNoun} from ${updated} contact${updated === 1 ? '' : 's'}.`,
        );
      } catch (err) {
        console.error('[ContactSelectionPreviewCard] apply failed', err);
        if (identityRef.current === requestIdentity) {
          setConfirmed(false);
          setReviewedExclusions(null);
          setSelectionReviewError(err instanceof Error && /changed after review|CONFLICT/i.test(err.message)
            ? 'This selection changed after review. Refresh it and confirm again.'
            : null);
          toast.error(err instanceof Error ? err.message : 'Failed to apply tags.');
        }
      } finally {
        if (identityRef.current === requestIdentity) setApplying(false);
      }
    },
    [matchedCount, organizationId, payload.selectionToken, reviewedExclusions, selectionIdentity, threadId],
  );

  const handleApply = useCallback(
    () => applyTags(selectedTags, action),
    [applyTags, selectedTags, action],
  );

  // Create a new org tag (adds it to the dictionary) and immediately apply it
  // to the selection. If the name already exists, just applies the existing one.
  const handleCreateAndApply = useCallback(async () => {
    const name = newTagName.trim();
    if (!name || applying || creating) return;
    if (name.length > MAX_TAG_LENGTH) {
      toast.error(`Tag must be ${MAX_TAG_LENGTH} characters or fewer.`);
      return;
    }
    if (!TAG_PATTERN.test(name)) {
      toast.error(
        'Tag can only contain letters, numbers, spaces, hyphens, underscores, and ampersands.',
      );
      return;
    }
    const existing = dictionary.find((t) => t.toLowerCase() === name.toLowerCase());
    if (existing) {
      await applyTags([existing], 'add');
      return;
    }
    if (!isAccountsRouteConfigured('PUT /org-contact-tags')) {
      toast.error('Accounts backend is not configured.');
      return;
    }
    setCreating(true);
    const requestIdentity = selectionIdentity;
    try {
      const token = await getAuthToken();
      if (identityRef.current !== requestIdentity) return;
      // PUT replaces the dictionary (names only); backend preserves usage_count
      // for surviving tags. Send the current list plus the new name.
      const res = await accountsClient.request('PUT /org-contact-tags', {
        authorization: token,
        input: { tags: [...dictionary, name] },
      });
      const data = await res.json().catch(() => ({}));
      if (identityRef.current !== requestIdentity) return;
      if (!res.ok) {
        throw new Error(
          ('error' in data && data.error) ||
            (res.status === 403
              ? 'You need an admin role to create tags.'
              : 'Failed to create tag'),
        );
      }
      setDictionary((prev) =>
        prev.some((t) => t.toLowerCase() === name.toLowerCase()) ? prev : [...prev, name],
      );
      toast.success(`Created tag "${name}".`);
      // New dictionary entry — let Settings → Tags refresh live.
      notifyOrgContactTagsUpdated();
      await applyTags([name], 'add');
    } catch (err) {
      console.error('[ContactSelectionPreviewCard] create tag failed', err);
      if (identityRef.current === requestIdentity) {
        toast.error(err instanceof Error ? err.message : 'Failed to create tag.');
      }
    } finally {
      if (identityRef.current === requestIdentity) setCreating(false);
    }
  }, [newTagName, applying, creating, dictionary, applyTags, selectionIdentity]);

  const replaceExcluded = (next: Set<string>) => {
    excludedIdsRef.current = next;
    setExcludedIds(next);
    setConfirmed(false);
    setReviewedExclusions(null);
    setSelectionReviewError(null);
    queueRef.current?.replace([...next]);
  };

  const toggleExcluded = (id: string) => {
    const next = new Set(excludedIdsRef.current);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    replaceExcluded(next);
  };

  const confirmReviewedSelection = () => {
    const snapshot = queueRef.current?.snapshot();
    if (!snapshot || exclusionsPendingRef.current || selectionReviewError) return;
    setReviewedExclusions(snapshot);
    setConfirmed(true);
  };

  // Only after the newest uncheck is saved, so the assistant reads the list shown.
  const useReviewedList = () => {
    const revision = exclusionRevisionRef.current;
    if (!onUseList || exclusionsPendingRef.current || revision === null || selectionReviewError) return;
    setUsedListRevision(revision);
    const requestIdentity = selectionIdentity;
    void onUseList(USE_LIST_MESSAGE).catch(() => false).then((sent) => {
      // A send that did not go out offers the button again.
      if (!sent && identityRef.current === requestIdentity) setUsedListRevision(null);
    });
  };

  const refreshSelectionReview = () => {
    queueRef.current?.stop();
    queueRef.current = null;
    exclusionHydratedRef.current = false;
    exclusionRevisionRef.current = null;
    exclusionsPendingRef.current = false;
    rowsCursorRef.current = 0;
    setRows([]);
    setRowsDone(false);
    setRowsError(null);
    setSelectionHydrated(false);
    setSelectionReviewError(null);
    setReviewedExclusions(null);
    setConfirmed(false);
    void loadMoreRows(true);
  };
  const includedShownCount = shownRows.filter((c) => !excludedIds.has(c.id)).length;

  // No max-width here: the card fills the message column, which the panel
  // already width-caps (compact frame / maximized max-w) — so the table
  // widens when the chat is maximized.
  return (
    <div
      className="w-full rounded-lg border border-edge bg-white p-4 text-sm"
      data-testid="contact-selection-preview"
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-sage-light text-sage">
          <UsersIcon className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <div className="text-2xl font-semibold text-ink">
            {matchedCount.toLocaleString()}
            <span className="ml-1.5 text-sm font-normal text-ink-muted">
              contact{matchedCount === 1 ? '' : 's'} match
            </span>
          </div>
          <p className="mt-0.5 text-sm text-ink-secondary">{payload.criteriaEcho}</p>
        </div>
      </div>

      {(payload.warnings || []).map((warning) => (
        <p
          key={warning}
          className="mt-3 rounded-md border border-[#F59E0B]/40 bg-[#FFFBEB] px-3 py-2 text-xs text-[#92400E]"
        >
          {warning}
        </p>
      ))}

      {shownRows.length > 0 && (
        <div className="assistant-record-container mt-3 overflow-hidden rounded-md border border-edge">
          <div className="max-h-[300px] overflow-y-auto">
            <table className="assistant-contact-records w-full text-left text-xs">
              {/* z-10: row checkboxes are positioned elements later in the
                  DOM — without a z-index they paint over the sticky header
                  (and its select-all checkbox) while scrolling. */}
              <thead className="sticky top-0 z-10 bg-paper-inset text-ink-muted">
                <tr>
                  <th className="w-10 px-3 py-1.5">
                    {canExclude && !result && (
                      <Checkbox
                        ariaLabel="Select all shown"
                        checked={includedShownCount === shownRows.length}
                        indeterminate={
                          includedShownCount > 0 && includedShownCount < shownRows.length
                        }
                        onChange={(nextChecked: boolean) => {
                          const next = new Set(excludedIdsRef.current);
                          for (const row of shownRows) {
                            if (nextChecked) next.delete(row.id);
                            else next.add(row.id);
                          }
                          replaceExcluded(next);
                        }}
                        disabled={applying || !selectionHydrated || Boolean(selectionReviewError)}
                      />
                    )}
                  </th>
                  <th className="px-3 py-1.5 font-medium">Name</th>
                  <th className="px-3 py-1.5 font-medium">Email</th>
                  <th className="px-3 py-1.5 font-medium">Phone</th>
                  <th className="px-3 py-1.5 font-medium">Location</th>
                </tr>
              </thead>
              <tbody>
                {shownRows.map((contact) => {
                  const included = !excludedIds.has(contact.id);
                  return (
                    <tr
                      key={contact.id}
                      className={`border-t border-edge ${included ? '' : 'opacity-40'}`}
                    >
                      <td className="px-3 py-1.5 align-top">
                        {canExclude && !result && (
                          <Checkbox
                            ariaLabel={`Include ${contact.fullName || contact.email || contact.id}`}
                            checked={included}
                            onChange={() => toggleExcluded(contact.id)}
                            disabled={applying || !selectionHydrated || Boolean(selectionReviewError)}
                          />
                        )}
                      </td>
                      <td className="px-3 py-1.5 text-ink-secondary">{contact.fullName || '—'}</td>
                      <td
                        className="max-w-[180px] truncate px-3 py-1.5 text-ink-muted"
                        title={contact.email}
                        data-empty={!contact.email}
                      >
                        {contact.email || '—'}
                      </td>
                      <td data-empty={!contact.phone} className="px-3 py-1.5 text-ink-muted">{contact.phone || '—'}</td>
                      <td
                        className="max-w-[180px] truncate px-3 py-1.5 text-ink-muted"
                        title={contact.location}
                        data-empty={!contact.location}
                      >
                        {contact.location || '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {(matchedCount > shownRows.length || rowsLoading) && (
            <div className="flex items-center justify-between gap-2 bg-paper-inset px-3 py-1.5 text-[11px] text-ink-muted">
              <span>
                {rowsError && rows.length === 0
                  ? `Showing a ${shownRows.length.toLocaleString()}-contact sample of ${matchedCount.toLocaleString()} — couldn't load the full list (${rowsError.replace(/\.$/, '')}). Ask again to refresh it.`
                  : `Showing ${shownRows.length.toLocaleString()} of ${matchedCount.toLocaleString()}.${canExclude ? ' Uncheck a contact to exclude it.' : ''}`}
              </span>
              {!rowsDone && rows.length > 0 && (
                <button
                  type="button"
                  onClick={() => void loadMoreRows()}
                  disabled={rowsLoading}
                  className="shrink-0 font-medium text-sage hover:underline disabled:opacity-50"
                >
                  {rowsLoading ? 'Loading…' : 'Load more'}
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Result state */}
      {result ? (
        <div className="mt-3 rounded-md border border-sage/40 bg-sage-light/40 px-3 py-2 text-sm text-ink-secondary">
          {action === 'add' ? 'Tagged' : 'Untagged'} {result.updated.toLocaleString()} contact
          {result.updated === 1 ? '' : 's'}
          {result.unchanged > 0 ? ` · ${result.unchanged.toLocaleString()} already set` : ''}
          {result.skipped > 0 ? ` · ${result.skipped.toLocaleString()} skipped` : ''}.
        </div>
      ) : !canApply ? (
        <p className="mt-3 text-xs text-ink-muted">
          {unavailableSelectionText(purpose, matchedCount, Boolean(payload.overCap), cap)}
        </p>
      ) : purpose === 'lookup' ? null : selectionReviewError ? (
        <div className="assistant-selection-review-alert" role="alert">
          <p>{selectionReviewError}</p>
          <Button variant="neutral" size="workspace" onClick={refreshSelectionReview}>
            Refresh review
          </Button>
        </div>
      ) : purpose === 'segment' ? (
        <div className="mt-3">
          <p className="text-xs text-ink-muted" role="status">
            {exclusionsPending
              ? 'Saving your changes…'
              : onUseList
                ? 'Uncheck anyone to leave out, then choose Use this list.'
                : 'Uncheck anyone to leave out, then tell the assistant the list is ready.'}
          </p>
          {onUseList && (
            <Button
              variant="primary"
              size="workspace"
              onClick={useReviewedList}
              disabled={
                useListDisabled ||
                matchedCount - excludedIds.size <= 0 ||
                exclusionsPending ||
                !selectionHydrated ||
                exclusionRevision === null ||
                usedListRevision === exclusionRevision
              }
              className="mt-2"
            >
              Use this list
            </Button>
          )}
        </div>
      ) : !confirmed ? (
        /* Step 1 — confirm the contact list. The tag chooser (create new /
           pick existing) only appears after this. */
        <div className="mt-3">
          <p className="text-xs text-ink-muted">
            Review the list above — uncheck anyone you don’t want tagged, then confirm to choose the
            tag. You can also just tell the assistant in chat.
          </p>
          <Button
            variant="primary"
            size="workspace"
            onClick={confirmReviewedSelection}
            disabled={
              matchedCount - excludedIds.size <= 0 ||
              exclusionsPending ||
              !selectionHydrated ||
              exclusionRevision === null
            }
            className="mt-2"
          >
            {exclusionsPending ? 'Saving exclusions…' : `Confirm ${(matchedCount - excludedIds.size).toLocaleString()} contact${matchedCount - excludedIds.size === 1 ? '' : 's'}`}
          </Button>
        </div>
      ) : (
        <>
          {/* Header row — "Choose a tag" title + the add/remove toggle,
              right-aligned. One row instead of a standalone toggle block. */}
          <div className="mt-3 flex items-center justify-between gap-3">
            <div className="text-base font-medium text-ink-secondary">Choose a tag</div>
            <div className="inline-flex rounded-md border border-edge p-0.5">
              {(['add', 'remove'] as ApplyAction[]).map((a) => (
                <button
                  key={a}
                  type="button"
                  onClick={() => setAction(a)}
                  disabled={applying}
                  className={`rounded px-3 py-1 text-xs font-medium capitalize transition-colors ${
                    action === a ? 'bg-sage-light text-sage' : 'text-ink-muted hover:bg-paper-muted'
                  }`}
                >
                  {a}
                </button>
              ))}
            </div>
          </div>

          {/* Create a new tag (add mode only) — pre-filled with the model's
              suggestion when the request implied a new tag. No inset box; the
              single filled-green CTA on this card is the Apply button below,
              so Create & apply stays a quiet outline button. */}
          {action === 'add' && (
            <div className="mt-3">
              <div className="mb-1.5 text-xs font-medium text-ink-secondary">New tag</div>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={newTagName}
                  onChange={(e) => setNewTagName(e.target.value)}
                  placeholder="e.g. Repeat Donor"
                  maxLength={MAX_TAG_LENGTH}
                  disabled={applying || creating}
                  className="min-w-0 flex-1 rounded-md border border-edge bg-white px-2.5 py-1 text-sm text-ink placeholder:text-ink-muted focus:outline-none focus:ring-1 focus:ring-sage"
                />
                <Button
                  onClick={handleCreateAndApply}
                  disabled={
                    applying ||
                    creating ||
                    exclusionsPending ||
                    !reviewedExclusions ||
                    !newTagName.trim() ||
                    matchedCount - excludedIds.size <= 0
                  }
                  className="shrink-0 rounded-md border border-edge-input bg-white px-3 py-1 text-sm font-medium text-ink-secondary hover:bg-paper-muted disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {creating ? 'Creating…' : 'Create & apply'}
                </Button>
              </div>
              {/* Suggested names — dashed "＋" chips read as "new", so the
                  Suggestions:/or-type-your-own labels are unnecessary. */}
              {(payload.suggestedTagNames?.length ?? 0) > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {payload.suggestedTagNames!.map((name) => (
                    <button
                      key={name}
                      type="button"
                      onClick={() => setNewTagName(name)}
                      disabled={applying || creating}
                      className={`rounded-full border border-dashed px-2.5 py-0.5 text-[11px] transition-colors ${
                        newTagName.trim() === name
                          ? 'border-sage bg-sage-light text-sage'
                          : 'border-edge bg-white text-ink-secondary hover:bg-paper-muted'
                      }`}
                    >
                      ＋ {name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Tag picker (existing tags) */}
          {action === 'add' && dictionary.length > 0 && (
            <div className="mt-4 text-xs font-medium text-ink-secondary">
              Or pick an existing tag
            </div>
          )}
          {dictionary.length > 0 ? (
            <div className="mt-2 flex flex-wrap gap-2">
              {dictionary.map((tag) => {
                const isSelected = selectedTags.includes(tag);
                return (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => toggleTag(tag)}
                    disabled={applying}
                    aria-pressed={isSelected}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition-colors ${
                      isSelected
                        ? 'border-sage bg-sage-light text-sage'
                        : 'border-edge bg-paper-inset text-ink-secondary hover:bg-paper-muted'
                    }`}
                  >
                    <TagIcon className="h-3 w-3" />
                    {tag}
                  </button>
                );
              })}
            </div>
          ) : (
            <p className="mt-2 text-xs text-ink-muted">
              {action === 'add'
                ? 'No existing tags yet — create one above.'
                : 'No tags in your dictionary to remove.'}
            </p>
          )}

          {progress && applying && (
            <p className="mt-2 flex items-center gap-1.5 text-xs text-ink-muted">
              <span
                className="inline-block h-3 w-3 animate-spin rounded-full border-[1.5px] border-sage border-t-transparent"
                aria-hidden="true"
              />
              Applying… {progress.done.toLocaleString()} / {progress.total.toLocaleString()}
            </p>
          )}

          <div className="mt-5 flex justify-end">
            <Button
              variant={action === 'remove' ? 'danger' : 'primary'}
              size="workspace"
              onClick={handleApply}
              disabled={
                applying || selectedTags.length === 0 || matchedCount - excludedIds.size <= 0
                || exclusionsPending || !reviewedExclusions || reviewedExclusions.revision !== exclusionRevision
              }
            >
              {applying
                ? action === 'add'
                  ? 'Adding…'
                  : 'Removing…'
                : `${action === 'add' ? 'Add' : 'Remove'} ${selectedTags.length || ''} ${
                    selectedTags.length === 1 ? 'tag' : 'tags'
                  } · ${(matchedCount - excludedIds.size).toLocaleString()} contact${
                    matchedCount - excludedIds.size === 1 ? '' : 's'
                  }`.replace('  ', ' ')}
            </Button>
          </div>
          <p className="mt-2 text-xs text-ink-muted">
            Or tell the assistant: “apply Repeat Donor to these contacts”.
          </p>
        </>
      )}
    </div>
  );
}

function unavailableSelectionText(purpose: 'tag' | 'lookup' | 'segment', matchedCount: number, overCap: boolean, cap: number) {
  if (purpose === 'tag') {
    if (matchedCount === 0) return 'No contacts matched, so there is nothing to tag.';
    if (overCap) return `This selection is above the ${cap.toLocaleString()} limit for one tagging action. Narrow it, then try again.`;
    return 'This selection can no longer be applied — re-run the request to refresh it.';
  }
  if (matchedCount === 0) return 'No contacts matched.';
  if (overCap) return `This list is above the ${cap.toLocaleString()}-contact limit, so only a sample is shown. Narrow it to see everyone.`;
  return 'This list can no longer be loaded — ask again to refresh it.';
}
